import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  makePermissionCheckerForCurrentSub,
  makeRoleCheckerForCurrentSub,
} from 'src/redux-store/thunks/subscriptions.thunks';

import { TestID } from '../testID';

import type { RootState } from '../types/state.types';
import type { DataEntityTab } from '../types/dataEntitiesTabs.types';

export const useDatahubTabs = (subscriptionId: string) => {
  const subscriptionInfoStatus = useSelector((state: RootState) => state.subscription.info?.status);
  const roleChecker = useSelector((state: RootState) => makeRoleCheckerForCurrentSub(state));
  const permissionChecker = useSelector((state: RootState) =>
    makePermissionCheckerForCurrentSub(state)
  );

  const tabs = useMemo(() => {
    const isAdmin = roleChecker('group:Admin');
    const canModifySubscription = permissionChecker('modify_subscription');
    const shouldDisplayPermissions =
      subscriptionInfoStatus &&
      subscriptionInfoStatus['authentication-is-required'] &&
      permissionChecker('modify_subscription');

    const nav: DataEntityTab[] = [
      {
        id: 'queues',
        shouldDisplay: true,
        label: 'Queues',
        to: `/subscription/${subscriptionId}/settings-datahub/queues`,
        tooltip: 'Queues',
        testId: TestID.DatasetsQueuesTab,
      },
      {
        id: 'logs',
        shouldDisplay: true,
        label: 'Logs',
        to: `/subscription/${subscriptionId}/settings-datahub/logs`,
        tooltip: 'Logs',
        testId: TestID.DatasetsLogsTab,
      },
      {
        id: 'variables',
        shouldDisplay: isAdmin,
        label: 'Variables',
        to: `/subscription/${subscriptionId}/settings-datahub/variables`,
        tooltip: 'Variables',
        testId: TestID.DatasetsVariablesTab,
      },
      {
        id: 'tools',
        shouldDisplay: canModifySubscription,
        label: 'Tools',
        to: `/subscription/${subscriptionId}/settings-datahub/tools`,
        tooltip: 'Tools',
        testId: TestID.DatasetsToolsTab,
      },
      {
        id: 'metadata',
        shouldDisplay: isAdmin,
        label: 'Metadata',
        to: `/subscription/${subscriptionId}/settings-datahub/metadata`,
        tooltip: 'Metadata',
        testId: TestID.DatasetsUserTab,
      },
      {
        id: 'permissions',
        shouldDisplay: shouldDisplayPermissions,
        label: 'Permissions',
        to: `/subscription/${subscriptionId}/settings-datahub/permissions`,
        tooltip: 'Permissions',
        testId: TestID.DatasetsPermissionsTab,
      },
      {
        id: 'stacktrace',
        shouldDisplay: isAdmin,
        label: 'Stacktrace',
        to: `/subscription/${subscriptionId}/settings-datahub/stacktrace`,
        tooltip: 'Stacktrace',
        testId: TestID.DatasetsStacktraceTab,
      },
      {
        id: 'user-datasets',
        shouldDisplay: true,
        label: 'User datasets',
        to: `/subscription/${subscriptionId}/settings-datahub/user-datasets`,
        tooltip: 'User datasets',
        testId: TestID.DatasetsUserTab,
      },
      {
        id: 'internal-datasets',
        shouldDisplay: true,
        label: 'Internal datasets',
        to: `/subscription/${subscriptionId}/settings-datahub/internal-datasets`,
        tooltip: 'Internal datasets',
        testId: TestID.DatasetsInternalTab,
      },
    ];

    return nav.filter((tab) => tab.shouldDisplay);
  }, [subscriptionId, subscriptionInfoStatus]);

  return tabs;
};
